import React, { useMemo } from 'react';
import Icon from '../AppIcon';

const requirements = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p?.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter (A–Z)', test: (p) => /[A-Z]/?.test(p) },
  { id: 'lowercase', label: 'One lowercase letter (a–z)', test: (p) => /[a-z]/?.test(p) },
  { id: 'number', label: 'One number (0–9)', test: (p) => /[0-9]/?.test(p) },
  { id: 'special', label: 'One special character (!@#$…)', test: (p) => /[^A-Za-z0-9]/?.test(p) },
];

const getStrength = (password) => {
  if (!password) return 0;
  return requirements?.filter((r) => r?.test(password))?.length;
};

const strengthConfig = [
  { label: '', color: '#333333', bg: '#333333' },
  { label: 'Very Weak', color: '#ef4444', bg: '#ef4444' },
  { label: 'Weak', color: '#f97316', bg: '#f97316' },
  { label: 'Fair', color: '#eab308', bg: '#eab308' },
  { label: 'Strong', color: '#22c55e', bg: '#22c55e' },
  { label: 'Very Strong', color: '#16a34a', bg: '#16a34a' },
];

/**
 * PasswordStrengthMeter
 * Props:
 *   password {string} - the password to evaluate
 *   darkTheme {boolean} - use dark background styling (for reset-password page)
 */
const PasswordStrengthMeter = ({ password = '', darkTheme = false }) => {
  const strength = useMemo(() => getStrength(password), [password]);
  const config = strengthConfig?.[strength];
  const metFn = (req) => req?.test(password);

  if (!password) return null;

  const barBg = darkTheme ? '#2A2A2A' : '#e5e7eb';
  const textMuted = darkTheme ? '#999999' : 'var(--color-muted-foreground)';
  const textBase = darkTheme ? '#CCCCCC' : 'var(--color-foreground)';
  const checkBg = darkTheme ? '#1A1A1A' : 'transparent';
  const borderColor = darkTheme ? '#333333' : '#e5e7eb';

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex gap-1">
          {[1, 2, 3, 4, 5]?.map((i) => (
            <div
              key={i}
              className="h-1.5 flex-1 rounded-full transition-all duration-300"
              style={{ background: i <= strength ? config?.bg : barBg }}
            />
          ))}
        </div>
        {strength > 0 && (
          <span className="text-xs font-medium w-20 text-right" style={{ color: config?.color }}>
            {config?.label}
          </span>
        )}
      </div>
      {/* Requirements checklist */}
      <div
        className="rounded-lg p-3 space-y-1.5"
        style={{ background: darkTheme ? '#111111' : '#f9fafb', border: `1px solid ${borderColor}` }}
      >
        <p className="text-xs font-medium mb-2" style={{ color: textMuted }}>Password requirements:</p>
        {requirements?.map((req) => {
          const met = metFn(req);
          return (
            <div key={req?.id} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200"
                style={{
                  background: met ? 'rgba(34,197,94,0.15)' : checkBg,
                  border: `1px solid ${met ? '#22c55e' : (darkTheme ? '#444444' : '#d1d5db')}`,
                }}
              >
                {met && <Icon name="Check" size={10} color="#22c55e" />}
              </div>
              <span
                className="text-xs transition-colors duration-200"
                style={{ color: met ? (darkTheme ? '#86efac' : '#16a34a') : textMuted }}
              >
                {req?.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { getStrength, requirements };
export default PasswordStrengthMeter;
